import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, IconButton, CircularProgress, Tooltip, Collapse,
} from '@mui/material';
import {
  Favorite, PlaylistAdd, LibraryAdd, ExpandMore, ExpandLess,
} from '@mui/icons-material';
import AddToPlaylistDialog from './AddToPlaylistDialog';
import { useTranslation } from 'react-i18next';
import {
  SpotifySavedTrack, getSpotifySavedTracksCount, getSpotifySavedTracksPage,
  playSpotifyTrack, queueAdd, spotifyUnlikeTrack,
} from '../../services/sonosApi';
import { TrackArt, TrackPlayButton } from './sonosTrackUi';
import { SONOS_BLUE, SURFACE_BORDER } from './sonosUiStyles';
import { SonosMessageHandler } from './sonosFeedback';

const PAGE_SIZE = 50;
const COUNT_DEFER_MS = 1200;
const LOAD_MORE_ROOT_MARGIN = '120px';

function getScrollParent(el: HTMLElement | null): Element | null {
  let node = el?.parentElement ?? null;
  while (node) {
    if (node.hasAttribute('data-audio-inner-scroll')) return node;
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
}

interface SavedTrackRowProps {
  track: SpotifySavedTrack;
  canControl: boolean;
  onUnlike: (id: string) => void;
  onAddToQueue: (uri: string) => void;
  onAddToPlaylist: (uri: string) => void;
  onPlay: (uri: string) => void;
}

const SavedTrackRow = memo<SavedTrackRowProps>(({
  track, canControl, onUnlike, onAddToQueue, onAddToPlaylist, onPlay,
}) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        py: 0.75,
        borderBottom: `1px solid ${SURFACE_BORDER}`,
        touchAction: 'pan-y',
        contentVisibility: 'auto',
        containIntrinsicSize: '0 52px',
      }}
    >
      <TrackArt url={track.image_url} size={40} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          color: '#fff',
          fontSize: 13,
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {track.name}
        </Typography>
        <Typography sx={{
          color: '#999',
          fontSize: 11,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {track.artist}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.15, flexShrink: 0 }}>
        <Tooltip title={t('musicSystem.search.unlikeTrack')}>
          <IconButton size="small" onClick={() => onUnlike(track.id)} sx={{ color: '#e91e63', p: 0.35 }}>
            <Favorite sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('musicSystem.search.addToQueue')}>
          <span>
            <IconButton size="small" onClick={() => onAddToQueue(track.uri)} disabled={!canControl} sx={{ color: '#aaa', p: 0.35 }}>
              <PlaylistAdd sx={{ fontSize: 22 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('musicSystem.search.addToPlaylist')}>
          <span>
            <IconButton size="small" onClick={() => onAddToPlaylist(track.uri)} disabled={!canControl} sx={{ color: '#aaa', p: 0.35 }}>
              <LibraryAdd sx={{ fontSize: 22 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('musicSystem.playlists.detail.playPlaylist')}>
          <span>
            <TrackPlayButton size="compact" disabled={!canControl} onClick={() => onPlay(track.uri)} />
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
});

SavedTrackRow.displayName = 'SavedTrackRow';

interface SpotifySavedTracksSectionProps {
  speakerId: number | null;
  crossfade: boolean;
  canControl?: boolean;
  onMessage?: SonosMessageHandler;
  refreshKey?: number;
  filterQuery?: string;
  isDemoUser?: boolean;
  onDemoActionAttempt?: () => void;
  /** Si se define, reemplaza playSpotifyTrack / cola Sonos (p. ej. reproducción local). */
  onPlayUri?: (uri: string) => Promise<void> | void;
  onQueueUri?: (uri: string) => Promise<void> | void;
}

const SpotifySavedTracksSection: React.FC<SpotifySavedTracksSectionProps> = ({
  speakerId,
  crossfade,
  canControl = true,
  onMessage,
  refreshKey = 0,
  filterQuery = '',
  isDemoUser = false,
  onDemoActionAttempt,
  onPlayUri,
  onQueueUri,
}) => {
  const blockDemo = () => {
    if (!isDemoUser) return false;
    onDemoActionAttempt?.();
    return true;
  };
  const { t } = useTranslation();
  const [total, setTotal] = useState<number | null>(null);
  const [tracks, setTracks] = useState<SpotifySavedTrack[]>([]);
  const [loadingCount, setLoadingCount] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [addToPlaylistOpen, setAddToPlaylistOpen] = useState(false);
  const [addToPlaylistUri, setAddToPlaylistUri] = useState<string | null>(null);
  const deferTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const loadingPageRef = useRef(false);
  const hasMoreRef = useRef(false);
  const tracksLengthRef = useRef(0);

  loadingPageRef.current = loadingPage;
  hasMoreRef.current = hasMore;
  tracksLengthRef.current = tracks.length;

  const fetchCount = useCallback(async () => {
    setLoadingCount(true);
    try {
      setTotal(await getSpotifySavedTracksCount());
    } catch {
      setTotal(0);
    } finally {
      setLoadingCount(false);
    }
  }, []);

  const fetchPage = useCallback(async (offset: number, replace: boolean) => {
    setLoadingPage(true);
    try {
      const page = await getSpotifySavedTracksPage(offset, PAGE_SIZE);
      setTotal(page.total);
      setHasMore(page.has_more);
      setTracks((prev) => (replace ? page.tracks : [...prev, ...page.tracks]));
      setLoadedOnce(true);
    } catch {
      if (replace) setTracks([]);
      setHasMore(false);
    } finally {
      setLoadingPage(false);
    }
  }, []);

  useEffect(() => {
    if (deferTimerRef.current) clearTimeout(deferTimerRef.current);
    deferTimerRef.current = setTimeout(() => {
      void fetchCount();
    }, COUNT_DEFER_MS);
    return () => {
      if (deferTimerRef.current) clearTimeout(deferTimerRef.current);
    };
  }, [fetchCount, refreshKey]);

  useEffect(() => {
    if (!expanded || loadedOnce) return;
    void fetchPage(0, true);
  }, [expanded, loadedOnce, fetchPage]);

  const prevRefreshKeyRef = useRef(refreshKey);
  useEffect(() => {
    if (prevRefreshKeyRef.current === refreshKey) return;
    prevRefreshKeyRef.current = refreshKey;
    if (expanded) {
      void fetchPage(0, true);
    }
  }, [refreshKey, expanded, fetchPage]);

  useEffect(() => {
    if (!filterQuery.trim()) return;
    setExpanded(true);
    if (!loadedOnce) void fetchPage(0, true);
  }, [filterQuery, loadedOnce, fetchPage]);

  const visibleTracks = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    if (!q) return tracks;
    return tracks.filter((tr) => {
      const name = tr.name?.toLowerCase() ?? '';
      const artist = tr.artist?.toLowerCase() ?? '';
      return name.includes(q) || artist.includes(q);
    });
  }, [tracks, filterQuery]);

  const handleExpand = () => {
    setExpanded((v) => !v);
  };

  useEffect(() => {
    if (!expanded || !hasMore) return undefined;
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) return undefined;

    const root = getScrollParent(sentinel);
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (!hasMoreRef.current || loadingPageRef.current) return;
        void fetchPage(tracksLengthRef.current, false);
      },
      { root, rootMargin: LOAD_MORE_ROOT_MARGIN, threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [expanded, hasMore, fetchPage, tracks.length]);

  const playTrack = async (uri: string) => {
    if (blockDemo()) return;
    if (!canControl) {
      onMessage?.(t('musicSystem.playback.selectSpeaker'), 'error');
      return;
    }
    if (onPlayUri) {
      try {
        await onPlayUri(uri);
      } catch (e: any) {
        onMessage?.(e?.response?.data?.detail || e?.message || t('musicSystem.playback.playError'), 'error');
      }
      return;
    }
    if (!speakerId) {
      onMessage?.(t('musicSystem.playback.selectSpeaker'), 'error');
      return;
    }
    try {
      await playSpotifyTrack({ track_uri: uri, target_speaker_id: speakerId, crossfade });
    } catch (e: any) {
      onMessage?.(e?.response?.data?.detail || t('musicSystem.playback.playError'), 'error');
    }
  };

  const addToQueue = async (uri: string) => {
    if (blockDemo()) return;
    if (!canControl) {
      onMessage?.(t('musicSystem.playback.selectSpeaker'), 'error');
      return;
    }
    if (onQueueUri) {
      try {
        await onQueueUri(uri);
        onMessage?.(t('musicSystem.search.addedToQueue'), 'success');
      } catch (e: any) {
        onMessage?.(e?.response?.data?.detail || e?.message || t('musicSystem.playback.playError'), 'error');
      }
      return;
    }
    if (!speakerId) {
      onMessage?.(t('musicSystem.playback.selectSpeaker'), 'error');
      return;
    }
    try {
      await queueAdd(speakerId, { uri });
      onMessage?.(t('musicSystem.search.addedToQueue'), 'success');
    } catch (e: any) {
      onMessage?.(e?.response?.data?.detail || t('musicSystem.playback.playError'), 'error');
    }
  };

  const unlike = async (id: string) => {
    if (blockDemo()) return;
    try {
      await spotifyUnlikeTrack([id]);
      setTracks((prev) => prev.filter((tr) => tr.id !== id));
      setTotal((n) => (n != null && n > 0 ? n - 1 : 0));
    } catch (e: any) {
      onMessage?.(e?.response?.data?.detail || t('musicSystem.playback.playError'), 'error');
    }
  };

  const openAddToPlaylist = (uri: string) => {
    setAddToPlaylistUri(uri);
    setAddToPlaylistOpen(true);
  };

  if (!loadingCount && total === 0) return null;

  return (
    <Box sx={{
      mt: 2,
      mb: 2,
      contentVisibility: expanded ? 'visible' : 'auto',
      containIntrinsicSize: expanded ? undefined : '0 40px',
    }}>
      <Box
        onClick={handleExpand}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 0.75,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Favorite sx={{ color: '#e91e63', fontSize: 18 }} />
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 14, flex: 1 }}>
          {t('musicSystem.library.likedTracks')}
          {total != null && total > 0 && (
            <Typography component="span" sx={{ color: '#999', fontWeight: 600, fontSize: 12, ml: 0.75 }}>
              ({total})
            </Typography>
          )}
          {loadingCount && total == null && (
            <Typography component="span" sx={{ color: '#666', fontSize: 12, ml: 0.75 }}>…</Typography>
          )}
        </Typography>
        {expanded ? (
          <ExpandLess sx={{ color: '#999', fontSize: 20 }} />
        ) : (
          <ExpandMore sx={{ color: '#999', fontSize: 20 }} />
        )}
      </Box>

      <Collapse in={expanded} unmountOnExit>
        {loadingPage && tracks.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} sx={{ color: SONOS_BLUE }} />
          </Box>
        ) : (
          <>
            {visibleTracks.map((tr) => (
              <SavedTrackRow
                key={tr.id}
                track={tr}
                canControl={canControl}
                onUnlike={(id) => { void unlike(id); }}
                onAddToQueue={(uri) => { void addToQueue(uri); }}
                onAddToPlaylist={openAddToPlaylist}
                onPlay={(uri) => { void playTrack(uri); }}
              />
            ))}
            {filterQuery.trim() && loadedOnce && !loadingPage && visibleTracks.length === 0 && (
              <Typography sx={{ color: '#999', fontSize: 12, py: 1, px: 0.5 }}>
                {t('musicSystem.playlists.noFilterResults')}
              </Typography>
            )}
            {hasMore && (
              <Box
                ref={loadMoreSentinelRef}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 1.5,
                  minHeight: 36,
                }}
              >
                {loadingPage && (
                  <CircularProgress size={20} sx={{ color: SONOS_BLUE }} />
                )}
              </Box>
            )}
          </>
        )}
      </Collapse>

      <AddToPlaylistDialog
        open={addToPlaylistOpen}
        trackUri={addToPlaylistUri}
        onClose={() => { setAddToPlaylistOpen(false); setAddToPlaylistUri(null); }}
        onAdded={() => onMessage?.(t('musicSystem.search.addedToPlaylist'), 'success')}
        onError={(msg) => onMessage?.(msg, 'error')}
      />
    </Box>
  );
};

export default SpotifySavedTracksSection;
